import type { CSSProperties, HTMLAttributes } from 'react';

interface ImgSlotProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Optional image URL. When set, renders a real `<img>` with the mono overlay. */
  src?: string;
  /** Required when `src` is set; ignored otherwise. */
  alt?: string;
  /** Corner stamp label rendered via `data-label`. */
  label?: string;
  /** CSS `aspect-ratio` value (e.g. `'4 / 5'`). */
  aspect?: string;
  /** When true, applies a grayscale filter to the image (used for residents). */
  mono?: boolean;
  /** Image object-fit. Defaults to `cover`. Use `contain` for posters. */
  fit?: 'cover' | 'contain';
  /** When true, image is loaded eagerly. Defaults to lazy. */
  eager?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * Placeholder image surface. When `src` is provided, renders a real image
 * with a desaturated/darkened filter and the noise overlay on top so it
 * blends with the mono design. Without `src`, renders the pure noise field.
 */
export default function ImgSlot({
  src,
  alt = '',
  label,
  aspect,
  mono,
  fit = 'cover',
  eager,
  className = '',
  style,
  ...rest
}: ImgSlotProps) {
  const mergedStyle: CSSProperties = aspect ? { ...style, aspectRatio: aspect } : (style ?? {});

  return (
    <div
      className={`imgslot ${className}`.trim()}
      data-label={label}
      data-has-image={src ? 'true' : undefined}
      data-mono={mono ? 'true' : undefined}
      style={mergedStyle}
      {...rest}
    >
      {src && (
        <img
          src={src}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          draggable={false}
          aria-hidden={alt === '' ? 'true' : undefined}
          style={{ objectFit: fit }}
        />
      )}
    </div>
  );
}
